import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, FileText, Users } from 'lucide-react';
import Footer from '@/components/Footer';
import logoViable from '@/assets/logo-viabiliza.png';

const ReporteSocial = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [obligaciones, setObligaciones] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cargarObligaciones();
  }, []);

  const cargarObligaciones = async () => {
    try {
      const { data, error } = await supabase
        .from('obligaciones_ambientales')
        .select('*')
        .eq('tipo', 'Social')
        .order('codigo', { ascending: true });

      if (error) throw error;
      setObligaciones(data || []);
    } catch (error) {
      console.error('Error cargando obligaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'Cumplido':
        return 'bg-green-500';
      case 'En Proceso':
        return 'bg-yellow-500';
      case 'Vencido':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={logoViable} alt="Logo Viabiliza" className="h-12" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Reporte Social</h1>
                <p className="text-sm text-gray-600">Formato ICA - Cumplimiento</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/reporte-integrado')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver
              </Button>
              <Button variant="destructive" onClick={signOut}>
                Cerrar Sesión
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              Seleccionar Obligación Social
            </CardTitle>
            <CardDescription>
              Seleccione una obligación para generar su reporte de cumplimiento en formato ICA
            </CardDescription>
          </CardHeader>
        </Card>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : obligaciones.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No hay obligaciones sociales registradas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {obligaciones.map((obligacion) => (
              <Card key={obligacion.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{obligacion.codigo}</CardTitle>
                      <CardDescription className="mt-2">
                        {obligacion.nombre_programa}
                      </CardDescription>
                    </div>
                    <Badge className={getEstadoColor(obligacion.estado)}>
                      {obligacion.estado}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold">Frecuencia:</span> {obligacion.frecuencia}
                    </p>
                    {obligacion.responsable_interno && (
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Responsable:</span> {obligacion.responsable_interno}
                      </p>
                    )}
                  </div>
                  <Button 
                    className="w-full" 
                    onClick={() => navigate(`/reporte-social/${obligacion.id}`)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Generar Reporte
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ReporteSocial;
